import { useRef } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, Loader2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DEFAULT_UNCLAIMED_SORT,
  type UnclaimedInvoiceRow,
  type UnclaimedListResponse,
  type UnclaimedSortBy,
  type UnclaimedSortDir,
} from '@/hooks/useUnclaimed';
import { financesTable } from '@/lib/financesTypography';
import { nextSortState } from '@/lib/tableSort';
import { formatUnclaimedTravelTypeDisplay } from '@/components/unclaimed/unclaimedTravelType';

interface UnclaimedInvoicesTableProps {
  data?: UnclaimedListResponse;
  isLoading: boolean;
  page: number;
  pageSize: number;
  sortBy: UnclaimedSortBy;
  sortDir: UnclaimedSortDir;
  /**
   * Set of composite booking-line keys (`${invoiceNo}-${bookingNo}-${agentBkgNo}`)
   * the user has selected. The unclaimed pool stores multiple booking
   * lines under one monthly TRAMS invoice, so `tramsInvoiceNo` alone is
   * not row-unique.
   */
  selectedKeys: Set<string>;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSortChange: (sortBy: UnclaimedSortBy, sortDir: UnclaimedSortDir) => void;
  onToggleSelected: (row: UnclaimedInvoiceRow, on: boolean) => void;
  onClaimRow: (row: UnclaimedInvoiceRow) => void;
}

const compositeRowKey = (row: {
  tramsInvoiceNo: number;
  tramsBookingNo: number;
  tramsAgentBkgNo: number;
}): string =>
  `${row.tramsInvoiceNo}-${row.tramsBookingNo}-${row.tramsAgentBkgNo}`;

/**
 * Total Fare / Commission / Vendor Paid are intentionally NOT exposed in the
 * advisor-facing list — Mr. Pickles, 2026-05-08. Hiding them removes any
 * incentive to "shop" the unclaimed pool by amount before the support team
 * has reviewed eligibility. The fields are still on the wire (used in the
 * support email body) and pre-fill the create-invoice form once a claim is
 * in flight.
 */
const COLUMNS: Array<{
  key: UnclaimedSortBy | 'select' | 'actions';
  label: string;
  sortable: boolean;
  className?: string;
}> = [
  { key: 'select', label: '', sortable: false, className: 'w-10' },
  { key: 'primaryTraveler', label: 'Traveler', sortable: true },
  // AR-316: below `md` these collapse into a stacked summary under the
  // traveler name — the table needed 268px of sideways scrolling on a phone
  // and cut the "Confirm #" header in half.
  { key: 'supplierName', label: 'Supplier', sortable: true, className: 'hidden md:table-cell' },
  { key: 'travelType', label: 'Type', sortable: true, className: 'hidden md:table-cell' },
  { key: 'confirmationNumber', label: 'Confirm #', sortable: true, className: 'hidden md:table-cell' },
  { key: 'departureDate', label: 'Departure', sortable: true, className: 'hidden md:table-cell' },
  { key: 'returnDate', label: 'Return', sortable: true, className: 'hidden md:table-cell' },
  // AR-412: the invoice's TRAMS issue date = when it landed in the unclaimed
  // pool. Members check the report weekly and sort/filter on this.
  { key: 'issueDate', label: 'Issue Date', sortable: true, className: 'hidden md:table-cell' },
  { key: 'actions', label: '', sortable: false, className: 'w-24' },
];

/** Columns still rendered at the smallest breakpoint (select + traveler + actions). */
const MOBILE_COLUMN_COUNT = 3;

const PAGE_SIZE_OPTIONS = [25, 50, 100];

/** Sortable columns, offered in the phone-only sort control (headers are hidden below `md`). */
const MOBILE_SORT_OPTIONS = COLUMNS.filter((c) => c.sortable) as Array<{
  key: UnclaimedSortBy;
  label: string;
}>;
const DATE_SORT_KEYS: ReadonlySet<UnclaimedSortBy> = new Set([
  'departureDate',
  'returnDate',
  'issueDate',
]);
const DEFAULT_SORT_VALUE = '__default__';

function formatDate(raw: string | null): string {
  if (!raw) return '—';
  if (raw.length < 10) return '—';
  const iso = raw.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '—';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

export function UnclaimedInvoicesTable({
  data,
  isLoading,
  page,
  pageSize,
  sortBy,
  sortDir,
  selectedKeys,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onToggleSelected,
  onClaimRow,
}: UnclaimedInvoicesTableProps) {
  const rows = data?.records ?? [];
  const prevBtnRef = useRef<HTMLButtonElement>(null);
  const nextBtnRef = useRef<HTMLButtonElement>(null);
  const totalPages = data?.totalPages ?? 0;
  const total = data?.total ?? 0;

  const renderSortIcon = (col: UnclaimedSortBy) => {
    if (sortBy !== col) {
      return <ArrowUpDown aria-hidden="true" className="ml-1 inline h-3 w-3 text-gray-400" />;
    }
    return sortDir === 'asc' ? (
      <ArrowUp aria-hidden="true" className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown aria-hidden="true" className="ml-1 inline h-3 w-3" />
    );
  };

  // AR-412: A–Z → Z–A → default (was Z–A first, then a two-way toggle).
  const handleSortClick = (col: UnclaimedSortBy) => {
    const next = nextSortState({ sortBy, sortDir }, col, DEFAULT_UNCLAIMED_SORT);
    onSortChange(next.sortBy, next.sortDir);
  };

  const isDefaultSort =
    sortBy === DEFAULT_UNCLAIMED_SORT.sortBy &&
    sortDir === DEFAULT_UNCLAIMED_SORT.sortDir;
  const dirLabel = (col: UnclaimedSortBy, dir: UnclaimedSortDir) =>
    DATE_SORT_KEYS.has(col)
      ? dir === 'asc'
        ? 'Oldest first'
        : 'Newest first'
      : dir === 'asc'
        ? 'A–Z'
        : 'Z–A';

  return (
    <div className="space-y-3">
      {/* AR-412: column headers other than Traveler are hidden on a phone, so
          sorting needs its own thumb-sized control there. */}
      <div className="flex items-center gap-2 md:hidden">
        <Select
          value={isDefaultSort ? DEFAULT_SORT_VALUE : sortBy}
          onValueChange={(v) => {
            if (v === DEFAULT_SORT_VALUE) {
              onSortChange(DEFAULT_UNCLAIMED_SORT.sortBy, DEFAULT_UNCLAIMED_SORT.sortDir);
            } else {
              onSortChange(v as UnclaimedSortBy, 'asc');
            }
          }}
        >
          <SelectTrigger aria-label="Sort by" className="h-11 flex-1 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={DEFAULT_SORT_VALUE}>Sort: Default</SelectItem>
            {MOBILE_SORT_OPTIONS.map((o) => (
              <SelectItem key={o.key} value={o.key}>
                Sort: {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          className="h-11 min-w-[7.5rem] text-sm"
          disabled={isDefaultSort}
          aria-label={`Sort direction: ${dirLabel(sortBy, sortDir)}. Tap to reverse.`}
          onClick={() => onSortChange(sortBy, sortDir === 'asc' ? 'desc' : 'asc')}
        >
          {sortDir === 'asc' ? (
            <ArrowUp aria-hidden="true" className="mr-1 h-4 w-4" />
          ) : (
            <ArrowDown aria-hidden="true" className="mr-1 h-4 w-4" />
          )}
          {isDefaultSort ? 'Default' : dirLabel(sortBy, sortDir)}
        </Button>
      </div>
      <div className={`${financesTable.border} ${financesTable.shell}`}>
        {/* AR-210 (TC-16.1, TC-16.3): header scope, sort state, names for the
            select and actions columns, and a caption — all non-visual. */}
        <Table>
          <caption className="sr-only">
            Unclaimed commissions{total ? `, ${total} total` : ''}
          </caption>
          <TableHeader>
            <TableRow className="bg-gray-50 dark:bg-gray-800/50">
              {COLUMNS.map((col) => {
                if (col.key === 'select') {
                  return (
                    <TableHead key="select" scope="col" className={col.className}>
                      <span className="sr-only">Select</span>
                    </TableHead>
                  );
                }
                const sortKey = col.key as UnclaimedSortBy;
                const isSorted = col.sortable && sortBy === sortKey;
                return (
                  <TableHead
                    key={col.key}
                    scope="col"
                    className={col.className}
                    aria-sort={
                      col.sortable
                        ? isSorted
                          ? sortDir === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                        : undefined
                    }
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        className={financesTable.headSortable}
                        onClick={() =>
                          handleSortClick(sortKey)
                        }
                      >
                        {col.label}
                        <span className="sr-only">
                          {isSorted ? `, sorted ${sortDir === 'asc' ? 'ascending' : 'descending'}` : ', not sorted'}
                        </span>
                        {renderSortIcon(sortKey)}
                      </button>
                    ) : col.label ? (
                      <span className={financesTable.headStatic}>
                        {col.label}
                      </span>
                    ) : (
                      <span className="sr-only">Actions</span>
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={MOBILE_COLUMN_COUNT}
                  className="h-32 text-center md:hidden"
                >
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-gray-400" />
                </TableCell>
                <TableCell
                  colSpan={COLUMNS.length}
                  className="hidden h-32 text-center md:table-cell"
                >
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-gray-400" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={MOBILE_COLUMN_COUNT}
                  className="h-32 text-center text-sm text-gray-500 md:hidden"
                >
                  No unclaimed invoices match the current filters.
                </TableCell>
                <TableCell
                  colSpan={COLUMNS.length}
                  className="hidden h-32 text-center text-sm text-gray-500 md:table-cell"
                >
                  No unclaimed invoices match the current filters.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const key = compositeRowKey(row);
                const selected = selectedKeys.has(key);
                const claimable = row.claimStatus === 'available';
                return (
                  <TableRow
                    key={key}
                    data-state={selected ? 'selected' : undefined}
                    className={!claimable ? 'opacity-60' : undefined}
                  >
                    <TableCell>
                      {/* AR-412: the 16px box is wrapped in a 44px tap area on a phone. */}
                      <label className="-m-2 flex h-11 w-11 cursor-pointer items-center justify-center md:m-0 md:h-auto md:w-auto">
                        <Checkbox
                          checked={selected}
                          disabled={!claimable}
                          onCheckedChange={(checked) =>
                            onToggleSelected(row, checked === true)
                          }
                          aria-label={`Select TRAMS booking ${row.tramsInvoiceNo}/${row.tramsBookingNo}`}
                        />
                      </label>
                    </TableCell>
                    <TableCell className="font-medium">
                      <span className="block">{row.primaryTraveler ?? '—'}</span>
                      <span className="mt-1 flex flex-col gap-0.5 text-xs font-normal text-gray-500 dark:text-gray-400 md:hidden">
                        <span className="truncate">
                          {row.supplierName ?? '—'}
                          {row.travelType
                            ? ` · ${formatUnclaimedTravelTypeDisplay(row.travelType)}`
                            : ''}
                        </span>
                        {row.confirmationNumber && (
                          <span className="truncate">{row.confirmationNumber}</span>
                        )}
                        <span className="whitespace-nowrap">
                          {formatDate(row.departureDate)} – {formatDate(row.returnDate)}
                        </span>
                        <span className="whitespace-nowrap">
                          Issued {formatDate(row.issueDate)}
                        </span>
                      </span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{row.supplierName ?? '—'}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      {formatUnclaimedTravelTypeDisplay(row.travelType)}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {row.confirmationNumber ?? '—'}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{formatDate(row.departureDate)}</TableCell>
                    <TableCell className="hidden md:table-cell">{formatDate(row.returnDate)}</TableCell>
                    <TableCell className="hidden md:table-cell">{formatDate(row.issueDate)}</TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-11 md:h-9"
                        onClick={() => onClaimRow(row)}
                        disabled={!claimable}
                      >
                        Claim
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination footer */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-1 text-sm">
        <div className="text-gray-600 dark:text-gray-400" role="status" aria-live="polite" aria-atomic="true">
          {total > 0 ? (
            <>
              Page <span className="font-medium">{page}</span> of{' '}
              <span className="font-medium">{totalPages || 1}</span> · {total}{' '}
              total
            </>
          ) : (
            'No records'
          )}
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={String(pageSize)}
            onValueChange={(v) => onPageSizeChange(Number(v))}
          >
            <SelectTrigger aria-label="Rows per page" className="h-11 w-[80px] md:h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* AR-210 (TC-16.4): these used to become disabled during every fetch,
              which dropped focus to <body> on each page change. While loading they
              are aria-disabled instead (same look via the aria-disabled: classes). */}
          <Button
            ref={prevBtnRef}
            size="sm"
            variant="outline"
            className="h-11 aria-disabled:pointer-events-none aria-disabled:opacity-50 md:h-8"
            onClick={() => {
              if (isLoading) return;
              onPageChange(Math.max(1, page - 1));
              if (page - 1 <= 1) window.requestAnimationFrame(() => nextBtnRef.current?.focus());
            }}
            disabled={page <= 1}
            aria-disabled={isLoading || undefined}
          >
            Previous
          </Button>
          <Button
            ref={nextBtnRef}
            size="sm"
            variant="outline"
            className="h-11 aria-disabled:pointer-events-none aria-disabled:opacity-50 md:h-8"
            onClick={() => {
              if (isLoading) return;
              onPageChange(page + 1);
              if (page + 1 >= (totalPages || 1)) window.requestAnimationFrame(() => prevBtnRef.current?.focus());
            }}
            disabled={page >= (totalPages || 1)}
            aria-disabled={isLoading || undefined}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
